import { useState } from 'react';
import { COPY } from '../content';
import { apiGet, apiPost, type GameTab } from '../api';
import { useAsync } from '../hooks';
import { shortDate, monthLabel } from '../format';
import { Spinner, LoadError, Card, Pill } from '../components/ui';

/** Round a game number for display (miles/hours to 1 decimal, days whole). */
function fmt(n: number, metric: string): string {
  if (metric === 'days') return String(Math.round(n));
  return (Math.round(n * 10) / 10).toLocaleString('en-US');
}

function Bar({ value, goal }: { value: number; goal: number }) {
  const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  return (
    <div style={{ background: '#e6eaf0', borderRadius: 6, height: 10, overflow: 'hidden', margin: '6px 0' }}>
      <div style={{ width: `${pct}%`, background: '#1199FF', height: '100%' }} />
    </div>
  );
}

export function Game({ onChange }: { onChange: () => void }) {
  const { data, loading, error, reload } = useAsync(() => apiGet<GameTab>('/v1/admin/game'), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Create-challenge form state.
  const [period, setPeriod] = useState('');
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState<'days' | 'miles' | 'hours'>('days');
  const [goal, setGoal] = useState('');
  const [prize, setPrize] = useState('');
  const [teamOn, setTeamOn] = useState(false);
  const [teamTitle, setTeamTitle] = useState('');
  const [teamGoal, setTeamGoal] = useState('');
  const [created, setCreated] = useState(false);

  async function review(paddleId: string, action: 'approve' | 'deny') {
    setBusy(paddleId);
    setActionError(null);
    try {
      await apiPost(`/v1/admin/game/flagged/${paddleId}/review`, { action });
      reload();
      onChange();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function createChallenge() {
    setBusy('create');
    setActionError(null);
    setCreated(false);
    try {
      const body: Record<string, unknown> = { period, title, metric, goal: Number(goal) };
      if (prize.trim()) body.prize = prize.trim();
      if (teamOn && teamTitle.trim() && Number(teamGoal) > 0) {
        body.teamGoal = { title: teamTitle.trim(), metric: 'miles', goal: Number(teamGoal) };
      }
      await apiPost('/v1/admin/game/challenge', body);
      setCreated(true);
      setTitle('');
      setGoal('');
      setPrize('');
      setTeamTitle('');
      setTeamGoal('');
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <h1 className="screen-title">{COPY.game.title}</h1>
      <p className="screen-sub">{COPY.game.sub}</p>

      {loading ? <Spinner /> : null}
      {error ? <LoadError message={error} onRetry={reload} /> : null}
      {actionError ? <LoadError message={actionError} /> : null}

      {data ? (
        <>
          <Card title={`${COPY.game.challengeTitle} (${monthLabel(data.period)})`}>
            {data.challenge ? (
              <div>
                <strong>{data.challenge.title}</strong>
                <div className="small muted">
                  Goal: {fmt(data.challenge.goal, data.challenge.metric)} {data.challenge.metric} per member
                  {data.challenge.prize ? ` | Prize: ${data.challenge.prize}` : ''}
                </div>
                <Bar value={data.challenge.crewProgress} goal={data.challenge.goal} />
                <div className="small muted">
                  Crew so far: {fmt(data.challenge.crewProgress, data.challenge.metric)} {data.challenge.metric} |{' '}
                  {data.challenge.finishers} {COPY.game.finishers}
                </div>
              </div>
            ) : (
              <p className="muted small">{COPY.game.noChallenge}</p>
            )}
          </Card>

          <Card title={COPY.game.teamGoalTitle}>
            {data.teamGoal ? (
              <div>
                <strong>{data.teamGoal.title}</strong>
                <Bar value={data.teamGoal.progress} goal={data.teamGoal.goal} />
                <div className="small muted">
                  {fmt(data.teamGoal.progress, data.teamGoal.metric)} of {fmt(data.teamGoal.goal, data.teamGoal.metric)}{' '}
                  {data.teamGoal.metric}
                </div>
              </div>
            ) : (
              <p className="muted small">{COPY.game.noTeamGoal}</p>
            )}
          </Card>

          <Card title={COPY.game.flaggedTitle}>
            <p className="muted small">{COPY.game.flaggedSub}</p>
            {data.flagged.length === 0 ? <p className="muted small">{COPY.game.flaggedEmpty}</p> : null}
            {data.flagged.map((f) => (
              <div className="list-row" key={f.paddleId}>
                <div className="grow">
                  <div className="row" style={{ gap: 8 }}>
                    <strong>{f.memberName}</strong>
                    {f.crewNumber ? <span className="small muted">#{f.crewNumber}</span> : null}
                    {f.reviewStatus === 'requested' ? <Pill tone="amber">{COPY.game.reviewRequested}</Pill> : null}
                  </div>
                  <div className="small muted">
                    {shortDate(f.dateIso)} | {f.miles} mi | avg {f.avgMph} mph | max {f.maxMph} mph
                  </div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button className="primary" disabled={busy === f.paddleId} onClick={() => review(f.paddleId, 'approve')}>
                    {COPY.game.approve}
                  </button>
                  <button className="ghost" disabled={busy === f.paddleId} onClick={() => review(f.paddleId, 'deny')}>
                    {COPY.game.deny}
                  </button>
                </div>
              </div>
            ))}
          </Card>

          <Card title={COPY.game.createTitle}>
            <p className="muted small">{COPY.game.createSub}</p>
            {created ? <div className="banner" style={{ background: '#e7f6ec' }}>{COPY.game.created}</div> : null}
            <div className="form-col" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
              <label className="small muted">{COPY.game.period}
                <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-11" />
              </label>
              <label className="small muted">{COPY.game.cTitle}
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Paddle 8 days in November" />
              </label>
              <label className="small muted">{COPY.game.metric}
                <select value={metric} onChange={(e) => setMetric(e.target.value as 'days' | 'miles' | 'hours')}>
                  <option value="days">days</option>
                  <option value="miles">miles</option>
                  <option value="hours">hours</option>
                </select>
              </label>
              <label className="small muted">{COPY.game.goal}
                <input value={goal} onChange={(e) => setGoal(e.target.value)} inputMode="numeric" placeholder="8" />
              </label>
              <label className="small muted">{COPY.game.prize}
                <input value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="A shout out in the Crew Feed" />
              </label>
              <label className="small muted" style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={teamOn} onChange={(e) => setTeamOn(e.target.checked)} />
                {COPY.game.teamOn}
              </label>
              {teamOn ? (
                <>
                  <label className="small muted">{COPY.game.teamTitle}
                    <input value={teamTitle} onChange={(e) => setTeamTitle(e.target.value)} placeholder="Crew: 1000 miles together" />
                  </label>
                  <label className="small muted">{COPY.game.teamGoalValue} (miles)
                    <input value={teamGoal} onChange={(e) => setTeamGoal(e.target.value)} inputMode="numeric" placeholder="1000" />
                  </label>
                </>
              ) : null}
              <button
                className="primary"
                disabled={busy === 'create' || !period || !title || !goal}
                onClick={createChallenge}
              >
                {COPY.game.create}
              </button>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}
